/**
 * ClientChecklists — what the customer sees in their portal.
 *
 * One row per item, with drag-and-drop upload directly into the slot.
 * Once all required items are uploaded, a "Submit checklist" button
 * appears that notifies the firm.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch } from '../../fetchInterceptor';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import {
  ClipboardList,
  CheckCircle2,
  Clock,
  AlertCircle,
  FileCheck2,
  UploadCloud,
  Send,
  RotateCcw,
  Calendar,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  needed: { label: 'Needed', cls: 'bg-zinc-100 text-zinc-600 border-zinc-300', icon: AlertCircle },
  uploaded: { label: 'Uploaded', cls: 'bg-sky-100 text-sky-700 border-sky-200', icon: FileCheck2 },
  in_review: { label: 'In Review', cls: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock },
  approved: { label: 'Approved', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
  rejected: { label: 'Needs reupload', cls: 'bg-rose-100 text-rose-700 border-rose-200', icon: AlertCircle },
};

export default function ClientChecklists() {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchLists = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`${API_URL}/api/client-portal/checklists`);
      if (res.ok) {
        const data = await res.json();
        setLists(Array.isArray(data.checklists) ? data.checklists : []);
      }
    } catch {
      toast.error('Could not load checklists');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLists(); }, [fetchLists]);

  if (loading) {
    return (
      <div className="p-6 max-w-3xl mx-auto space-y-4">
        {[1, 2].map(i => <Skeleton key={i} className="h-40 w-full" />)}
      </div>
    );
  }

  if (lists.length === 0) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <Card>
          <CardContent className="text-center py-20 text-zinc-500">
            <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p className="text-base font-medium text-zinc-700">No checklists yet</p>
            <p className="text-sm mt-1">When your accountant sends one, it'll show up here.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6" data-testid="client-checklists-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <ClipboardList className="w-6 h-6 text-emerald-500" />
          My Checklists
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          Documents your accountant needs. Click any slot to upload.
        </p>
      </div>

      {lists.map(list => (
        <ChecklistCard key={list.checklist_id} list={list} onChanged={fetchLists} />
      ))}
    </div>
  );
}


function ChecklistCard({ list, onChanged }) {
  const [submitting, setSubmitting] = useState(false);
  const prog = list.progress || { completed: 0, total: 0 };
  const requiredItems = list.items.filter(i => i.required);
  const requiredDone = requiredItems.filter(i => ['uploaded', 'in_review', 'approved'].includes(i.status));
  const canSubmit = requiredItems.length > 0 && requiredDone.length === requiredItems.length && list.status !== 'submitted';

  const submit = async () => {
    if (!window.confirm('Submit this checklist? Your accountant will be notified.')) return;
    setSubmitting(true);
    try {
      const res = await apiFetch(`${API_URL}/api/client-portal/checklists/${list.checklist_id}/submit`, {
        method: 'POST',
      });
      if (res.ok) {
        toast.success('Checklist submitted! Your accountant has been notified.');
        onChanged();
      } else {
        toast.error('Could not submit');
      }
    } finally { setSubmitting(false); }
  };

  return (
    <Card data-testid={`client-checklist-${list.checklist_id}`}>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-lg">{list.title}</CardTitle>
            {list.description && <p className="text-sm text-zinc-500 mt-1">{list.description}</p>}
            {list.due_date && (
              <p className="text-xs text-amber-600 mt-1.5 flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" /> Due {new Date(list.due_date).toLocaleDateString()}
              </p>
            )}
          </div>
          {list.status === 'submitted' && (
            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
              <CheckCircle2 className="w-3 h-3 mr-1" /> Submitted
            </Badge>
          )}
        </div>
        <div className="mt-3 flex items-center gap-3">
          <div className="flex-1 h-2 bg-zinc-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-500 rounded-full transition-all"
              style={{ width: `${prog.total ? (prog.completed / prog.total) * 100 : 0}%` }}
            />
          </div>
          <span className="text-xs text-zinc-600 font-medium">
            {prog.completed} / {prog.total}
          </span>
        </div>
      </CardHeader>

      <CardContent className="space-y-2">
        {list.items.map(item => (
          <ItemRow
            key={item.item_id}
            item={item}
            checklistId={list.checklist_id}
            onChanged={onChanged}
            isSubmitted={list.status === 'submitted'}
          />
        ))}

        {canSubmit && (
          <Button
            onClick={submit}
            disabled={submitting}
            className="w-full mt-3 bg-emerald-600 hover:bg-emerald-700"
            data-testid={`submit-checklist-${list.checklist_id}`}
          >
            <Send className="w-4 h-4 mr-1.5" />
            {submitting ? 'Submitting…' : 'Submit Checklist'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}


function ItemRow({ item, checklistId, onChanged, isSubmitted }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const meta = STATUS_META[item.status] || STATUS_META.needed;
  const Icon = meta.icon;

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error('Max 10 MB');
    setUploading(true);
    const form = new FormData();
    form.append('file', file);
    try {
      const res = await apiFetch(
        `${API_URL}/api/client-portal/checklists/${checklistId}/items/${item.item_id}/upload`,
        { method: 'POST', body: form },
      );
      if (res.ok) {
        toast.success(`${item.label} uploaded`);
        onChanged();
      } else {
        toast.error('Upload failed');
      }
    } finally { setUploading(false); }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) upload(f);
  };

  const showUpload = !isSubmitted && ['needed', 'rejected'].includes(item.status);
  const showReupload = !isSubmitted && item.status !== 'approved' && item.upload_filename;

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); if (showUpload) setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={showUpload ? handleDrop : undefined}
      className={`border rounded-lg p-3 transition-colors ${
        dragOver ? 'border-emerald-500 bg-emerald-50' : 'border-zinc-200 bg-white'
      }`}
      data-testid={`client-item-${item.item_id}`}
    >
      <div className="flex items-start gap-3">
        <Icon className="w-4 h-4 mt-0.5 text-zinc-500 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-medium text-zinc-900">{item.label}</p>
            {item.required && (
              <Badge variant="outline" className="text-[10px] bg-rose-50 text-rose-700 border-rose-200">
                Required
              </Badge>
            )}
            <Badge variant="outline" className={`text-[10px] ${meta.cls}`}>{meta.label}</Badge>
          </div>
          {item.description && <p className="text-xs text-zinc-500 mt-0.5">{item.description}</p>}
          {item.upload_filename && (
            <p className="text-xs text-zinc-600 mt-1 truncate">📎 {item.upload_filename}</p>
          )}
        </div>
        <div className="flex-shrink-0">
          {showUpload && (
            <>
              <input
                ref={fileRef}
                type="file"
                hidden
                onChange={(e) => upload(e.target.files?.[0])}
                data-testid={`file-input-${item.item_id}`}
              />
              <Button
                size="sm"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid={`upload-btn-${item.item_id}`}
              >
                <UploadCloud className="w-3.5 h-3.5 mr-1" />
                {uploading ? 'Uploading…' : 'Upload'}
              </Button>
            </>
          )}
          {showReupload && !showUpload && (
            <>
              <input
                ref={fileRef}
                type="file"
                hidden
                onChange={(e) => upload(e.target.files?.[0])}
              />
              <Button
                size="sm"
                variant="outline"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid={`reupload-btn-${item.item_id}`}
              >
                <RotateCcw className="w-3.5 h-3.5 mr-1" />
                Replace
              </Button>
            </>
          )}
        </div>
      </div>
      {showUpload && (
        <p className="text-[11px] text-zinc-400 mt-2 pl-7">Tip: drag & drop a file onto this row.</p>
      )}
    </div>
  );
}
